import {APP_CONFIG} from "../../config/GeneralConfig";
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {sendUserToPage, ViewActionTypes} from "../common/UserViewSettingsActions";
import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, map, mergeMap} from "rxjs/operators";
import {ajax, AjaxResponse} from "rxjs/ajax";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {Identifier} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {
    ApiKeyId,
    UserApiKey,
    UserApiKeyProp,
    UserApiKeyRequest,
    UserApiKeyRequestProp
} from "../../models/api-keys/UserApiKey";
import {UserApiKeyModule} from "../../models/api-keys/UserApiKeyModule";

/**
 * Defines the combined set of all possible actions related to user API key management.
 *
 * This type supports actions for viewing, fetching, creating, and deleting API keys associated with a user.
 * It serves as a central type for managing and dispatching user API key-related actions within the application’s state management.
 *
 * By combining related action types, this type ensures consistent and type-safe handling of user API key operations.
 *
 * @typedef UserApiKeyActionTypes
 * @type {UserApiKeysViewActions | FetchUserApiKeyActionTypes | CreateUserApiKeyActionTypes | DeleteUserApiKeyActionTypes}
 */
export type UserApiKeyActionTypes = UserApiKeysViewActions
    | FetchUserApiKeyActionTypes
    | CreateUserApiKeyActionTypes
    | DeleteUserApiKeyActionTypes
    | ConfirmUserApiKeyCreationAction
/**
 * Generates the base path URL for accessing API keys within the system.
 *
 * This function provides a dynamic string representing the endpoint's base path.
 * It integrates with the application configuration to ensure consistency across environments.
 *
 * - The base path is constructed using the `baseWeb` property from the global `APP_CONFIG`.
 * - This ensures the resulting path is always environment-specific and resolves to the proper service endpoint.
 *
 * @return {string} The fully constructed base path for the "API Keys" service endpoint.
 * @throws {Error} If `APP_CONFIG.baseWeb` is undefined or improperly configured, this function will throw an error.
 */
const basePath = () => `${ APP_CONFIG.baseWeb }system/api_keys/`;

/**
 * Generates a complete path by appending a given ID to the base path.
 *
 * This function is used to construct a unique URL or resource path by attaching a specific identifier
 * (such as a user ID or resource ID) to a predefined base path. It ensures consistent path formatting
 * across the application.
 *
 * @param id - The identifier to append to the base path. Can be a number or a string.
 *             Ensure the ID is valid and non-empty, as invalid values might lead to incorrect paths.
 * @return A string representing the full path with the appended ID.
 * @throws Will not throw, but developers should handle cases where `id` is invalid
 *         (e.g., undefined, null, or improperly formatted data).
 */
const pathWithId = ( id: number | string ) => `${ basePath() }${ id }`;


/** Action dispatched to initiate fetching all user API keys. */
export interface FetchUserApiKeys extends ReduxAction<typeof FETCH_USER_API_KEYS> {}

/** Action dispatched when all user API keys are successfully fetched. */
export interface FetchUserApiKeysSuccess extends ReduxAction<typeof FETCH_USER_API_KEYS_SUCCESS> {
    /** The paginated API response containing the list of user API keys. */
    payload: ApiReturnType< UserApiKey[] >
}

/** Action dispatched when fetching all user API keys fails. */
export interface FetchUserApiKeysError extends ErrorAction<typeof FETCH_USER_API_KEYS_ERROR> {}

export const  FETCH_USER_API_KEYS = "FETCH_USER_API_KEYS";
export const  FETCH_USER_API_KEYS_SUCCESS = "FETCH_USER_API_KEYS_SUCCESS";
export const  FETCH_USER_API_KEYS_ERROR = "FETCH_USER_API_KEYS_ERROR";

/**
 * Creates an action to trigger fetching all user API keys.
 *
 * @returns A {@link FetchUserApiKeys} action.
 */
export const fetchUserApiKeys = (): FetchUserApiKeys =>
    ({ type: FETCH_USER_API_KEYS });

/**
 * Creates a success action containing the fetched API keys payload.
 *
 * @param payload - The API response wrapping the array of user API keys.
 * @returns A {@link FetchUserApiKeysSuccess} action.
 */
export const fetchUserApiKeysSuccess = ( payload: ApiReturnType< UserApiKey[] >  ): FetchUserApiKeysSuccess =>
    ({ type: FETCH_USER_API_KEYS_SUCCESS, payload });

/**
 * Creates an error action for a failed fetch-all request.
 *
 * @param error - The error encountered during the request.
 * @returns A {@link FetchUserApiKeysError} action.
 */
export const fetchUserApiKeysError = ( error: Error ): FetchUserApiKeysError =>
    ({ type: FETCH_USER_API_KEYS_ERROR, error });

/** Union of all action types related to fetching the full list of user API keys. */
export type FetchUserApiKeysActionTypes = FetchUserApiKeys | FetchUserApiKeysSuccess | FetchUserApiKeysError

/** Union of view-related actions and fetch-all API key actions, used by the list view epic. */
export type UserApiKeysViewActions = ViewActionTypes | FetchUserApiKeysActionTypes

/**
 * Redux-Observable epic that fetches all user API keys.
 *
 * Listens for {@link FETCH_USER_API_KEYS} actions, builds the request using the
 * current view settings from the store, and dispatches either a success or error action.
 *
 * @param action$ - The stream of {@link UserApiKeysViewActions}.
 * @param store$ - The current application state, used to derive view settings and request parameters.
 * @returns An observable emitting {@link FetchUserApiKeysSuccess} or error-related actions.
 */
export const fetchUserApiKeysEpic = (
    action$: Observable< UserApiKeysViewActions >, store$: StateObservable< AppState > ) =>
    action$.pipe(
        ofType( FETCH_USER_API_KEYS ),
        mergeMap( _ => {
            const request = getUserViewSettingsApiRequest( basePath(), UserApiKeyModule.identifier, store$ );

            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map( response => fetchUserApiKeysSuccess(
                    response.response as ApiReturnType< UserApiKey[] >
                ) ),
                catchError( error => of(
                    fetchUserApiKeysError( error ),
                    eventNotificationError( error, store$, UserApiKeyModule.identifier, "fetchUserApiKeys" )
                ) )
            );
        })
    );

/** Action dispatched to initiate fetching a single user API key. */
export interface FetchUserApiKeyAction extends ApiKeyId, ReduxAction<typeof FETCH_USER_API_KEY> {}

/** Action dispatched when a single user API key is successfully fetched. */
export interface FetchUserApiKeySuccessAction extends UserApiKeyProp, ReduxAction<typeof FETCH_USER_API_KEY_SUCCESS> {}

/** Action dispatched when fetching a single user API key fails. */
export interface FetchUserApiKeyErrorAction extends ApiKeyId, ErrorAction<typeof FETCH_USER_API_KEY_ERROR> {}

/** Union of all action types related to fetching a single user API key. */
export type FetchUserApiKeyActionTypes = FetchUserApiKeyAction | FetchUserApiKeyErrorAction | FetchUserApiKeySuccessAction

export const FETCH_USER_API_KEY = "FETCH_USER_API_KEY";
export const FETCH_USER_API_KEY_SUCCESS = "FETCH_USER_API_KEY_SUCCESS";
export const FETCH_USER_API_KEY_ERROR = "FETCH_USER_API_KEY_ERROR";

/**
 * Creates an action to fetch a single user API key by its identifier.
 *
 * @param apiKeyId - The unique identifier of the API key to fetch.
 * @returns A {@link FetchUserApiKeyAction} action.
 */
export const fetchUserApiKey = ( apiKeyId: Identifier<string> ): FetchUserApiKeyAction =>
    ({ type: FETCH_USER_API_KEY, apiKeyId });

/**
 * Creates a success action containing the fetched API key.
 *
 * @param apiKeyId - The fetched {@link UserApiKey} object.
 * @returns A {@link FetchUserApiKeySuccessAction} action.
 */
export const fetchUserApiKeySuccess = ( apiKeyId: UserApiKey ): FetchUserApiKeySuccessAction =>
    ({ type: FETCH_USER_API_KEY_SUCCESS, userApiKey: apiKeyId });

/**
 * Creates an error action for a failed single-key fetch request.
 *
 * @param apiKeyId - The identifier of the API key that failed to fetch.
 * @param error - The error encountered during the request.
 * @returns A {@link FetchUserApiKeyErrorAction} action.
 */
export const fetchUserApiKeyError = ( apiKeyId: Identifier<string>, error: Error ): FetchUserApiKeyErrorAction =>
    ({ type: FETCH_USER_API_KEY_ERROR, error, apiKeyId });

/**
 * Redux-Observable epic that fetches a single user API key by its identifier.
 *
 * Listens for {@link FETCH_USER_API_KEY} actions and performs a GET request
 * to the API key endpoint. Dispatches a success or error action accordingly.
 *
 * @param action$ - The stream of {@link FetchUserApiKeyAction}.
 * @param store$ - The current application state, used for error notification context.
 * @returns An observable emitting {@link FetchUserApiKeySuccessAction} or error-related actions.
 */
export const fetchUserApiKeyEpic = (
    action$: Observable< FetchUserApiKeyAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( FETCH_USER_API_KEY ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject( pathWithId( action.apiKeyId.rawValue ) ) )
            .pipe(
                map( response => fetchUserApiKeySuccess( response.response as UserApiKey ) ),
                catchError( error => of(
                    fetchUserApiKeyError( action.apiKeyId, error ),
                    eventNotificationError( error, store$, UserApiKeyModule.identifier, "fetchUserApiKey" )
                ) )
            )
        )
    );


/** Action dispatched to initiate creating a new user API key. */
export interface CreateUserApiKeyAction extends UserApiKeyRequestProp, ReduxAction< typeof CREATE_USER_API_KEY > {}

/** Action dispatched when a user API key is successfully created. */
export interface CreateUserApiKeySuccessAction extends ReduxAction< typeof CREATE_USER_API_KEY_SUCCESS > {
    createdKey: Identifier< string >
}

/** Action dispatched when creating a user API key fails. */
export interface CreateUserApiKeyErrorAction extends ErrorAction<typeof CREATE_USER_API_KEY_ERROR> {}

/** Union of all action types related to creating a user API key. */
export type CreateUserApiKeyActionTypes = CreateUserApiKeyErrorAction | CreateUserApiKeySuccessAction | CreateUserApiKeyAction

export const CREATE_USER_API_KEY = "CREATE_USER_API_KEY";
export const CREATE_USER_API_KEY_SUCCESS = "CREATE_USER_API_KEY_SUCCESS";
export const CREATE_USER_API_KEY_ERROR = "CREATE_USER_API_KEY_ERROR";

/**
 * Creates an action to initiate the creation of a new user API key.
 *
 * @param userApiKeyRequest - The request payload containing the new API key details.
 * @returns A {@link CreateUserApiKeyAction} action.
 */
export const createUserApiKey = ( userApiKeyRequest: UserApiKeyRequest ): CreateUserApiKeyAction =>
    ({ type: CREATE_USER_API_KEY, userApiKeyRequest });

/**
 * Creates a success action containing the newly created API key.
 *
 * @param createdKey - The created API key string.
 * @returns A {@link CreateUserApiKeySuccessAction} action.
 */
export const createUserApiKeySuccess = ( createdKey: Identifier< string > ): CreateUserApiKeySuccessAction =>
    ({ type: CREATE_USER_API_KEY_SUCCESS, createdKey });

/**
 * Creates an error action for a failed API key creation request.
 *
 * @param error - The error encountered during the request.
 * @returns A {@link CreateUserApiKeyErrorAction} action.
 */
export const createUserApiKeyError = ( error: Error ): CreateUserApiKeyErrorAction =>
    ({ type: CREATE_USER_API_KEY_ERROR, error });

/**
 * Redux-Observable epic that creates a new user API key.
 *
 * Listens for {@link CREATE_USER_API_KEY} actions, sends a POST request with the
 * API key request payload (including serialized `validUntil` and `groupIds`), and on
 * success refreshes the API keys list and navigates the user back to the main API keys page.
 *
 * @param action$ - The stream of {@link CreateUserApiKeyAction}.
 * @param store$ - The current application state, used for error notification context.
 * @returns An observable emitting success, refresh, notification, and navigation actions, or error-related actions.
 */
export const createUserApiKeyEpic = (
    action$: Observable< CreateUserApiKeyAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( CREATE_USER_API_KEY ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject(
                basePath(),
                RouteMethods.post,
                {
                    name: action.userApiKeyRequest.name,
                    description: action.userApiKeyRequest.description,
                    userId: action.userApiKeyRequest.user.id,
                    validUntil: action.userApiKeyRequest.validUntil.getTime(),
                    groupIds: action.userApiKeyRequest.userGroups.map( group => group.id )
                }
            ) )
                .pipe(
                    mergeMap( response => {
                        console.log( response )
                        return of(
                            createUserApiKeySuccess( response.response as Identifier<string> ),
                            fetchUserApiKeys(),
                            eventNotificationSuccess()
                        )
                    }),
                    catchError( error => of(
                        createUserApiKeyError( error ),
                        eventNotificationError( error, store$, UserApiKeyModule.identifier, "createUserApiKey" )
                    ) )
                )
        )
    );

/**
 * Represents an action to confirm the creation of a user's API key.
 *
 * This action is used to signal that a user has confirmed the creation of a new API key
 * in the application. It plays a key role in managing API key lifecycle events triggered
 * by user interactions, ensuring that the API key creation process is both auditable and controlled.
 *
 * @interface
 * @extends ReduxAction<typeof CONFIRM_USER_API_KEY_CREATION>
 */
export interface ConfirmUserApiKeyCreationAction extends ReduxAction<typeof CONFIRM_USER_API_KEY_CREATION> {}

/**
 * A constant representing the action to confirm the creation of a user's API key.
 *
 * This value is used to trigger or identify the confirmation process when a user chooses to create an API key.
 * Typically utilized in state management, action dispatching, or event handling systems to handle user-triggered API key creation.
 *
 * Developers should ensure this constant aligns with the naming conventions and system events to maintain consistency
 * across the codebase.
 */
export const CONFIRM_USER_API_KEY_CREATION = "CONFIRM_USER_API_KEY_CREATION";

/**
 * Confirms the user's initiation of an API key creation.
 *
 * This function is used to trigger a specific action that confirms the user's intent to create a new API key.
 * It is typically dispatched within the application's state management to update the system about the action.
 *
 * @return {ConfirmUserApiKeyCreationAction} An action object containing the type `CONFIRM_USER_API_KEY_CREATION`,
 * which signals the completion of the API key creation confirmation process.
 */
export function confirmUserApiKeyCreation(): ConfirmUserApiKeyCreationAction {
    return { type: CONFIRM_USER_API_KEY_CREATION };
}

/** Action dispatched to initiate deleting a user API key. */
export interface DeleteUserApiKeyAction extends ApiKeyId, ReduxAction<typeof DELETE_USER_API_KEY> {}

/** Action dispatched when a user API key is successfully deleted. */
export interface DeleteUserApiKeySuccessAction extends ReduxAction<typeof DELETE_USER_API_KEY_SUCCESS> {}

/** Action dispatched when deleting a user API key fails. */
export interface DeleteUserApiKeyErrorAction extends ErrorAction<typeof DELETE_USER_API_KEY_ERROR> {}

/** Union of all action types related to deleting a user API key. */
export type DeleteUserApiKeyActionTypes = DeleteUserApiKeyAction | DeleteUserApiKeyErrorAction | DeleteUserApiKeySuccessAction

export const DELETE_USER_API_KEY = "DELETE_USER_API_KEY";
export const DELETE_USER_API_KEY_SUCCESS = "DELETE_USER_API_KEY_SUCCESS";
export const DELETE_USER_API_KEY_ERROR = "DELETE_USER_API_KEY_ERROR";

/**
 * Creates an action to initiate the deletion of a user API key.
 *
 * @param id - The unique identifier of the API key to delete.
 * @returns A {@link DeleteUserApiKeyAction} action.
 */
export const deleteUserApiKey = ( id: Identifier<string> ): DeleteUserApiKeyAction =>
    ({ type: DELETE_USER_API_KEY, apiKeyId: id });

/**
 * Creates a success action indicating the API key was deleted.
 *
 * @returns A {@link DeleteUserApiKeySuccessAction} action.
 */
export const deleteUserApiKeySuccess = (): DeleteUserApiKeySuccessAction =>
    ({ type: DELETE_USER_API_KEY_SUCCESS });

/**
 * Creates an error action for a failed API key deletion request.
 *
 * @param error - The error encountered during the request.
 * @returns A {@link DeleteUserApiKeyErrorAction} action.
 */
export const deleteUserApiKeyError = ( error: Error ): DeleteUserApiKeyErrorAction =>
    ({ type: DELETE_USER_API_KEY_ERROR, error });

/**
 * Redux-Observable epic that deletes a user API key.
 *
 * Listens for {@link DELETE_USER_API_KEY} actions, sends a DELETE request to the
 * API key endpoint, and on success refreshes the API keys list and navigates the
 * user back to the main API keys page.
 *
 * @param action$ - The stream of {@link DeleteUserApiKeyAction}.
 * @param store$ - The current application state, used for error notification context.
 * @returns An observable emitting success, refresh, notification, and navigation actions, or error-related actions.
 */
export const deleteUserApiKeyEpic = (
    action$: Observable< DeleteUserApiKeyAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( DELETE_USER_API_KEY ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject(
                pathWithId( action.apiKeyId.rawValue ),
                RouteMethods.delete
            ) )
                .pipe(
                    mergeMap( response => of(
                        deleteUserApiKeySuccess(),
                        fetchUserApiKeys(),
                        eventNotificationSuccess(),
                        sendUserToPage(
                            RouteConfig.api_keys.routes.main.path,
                            UserApiKeyModule.identifier
                        )
                    ) ),
                    catchError( error => of(
                        deleteUserApiKeyError( error ),
                        eventNotificationError( error, store$, UserApiKeyModule.identifier, "deleteUserApiKey" )
                    ) )
                )
        )
    );

